import { Injectable } from '@angular/core';
import { ApiService } from '@services/api.service';
import { CountryModel, CurrencyModel, IncoTermModel, UnitModel } from '@data-layer/models/common';
import { finalize, switchMap, take } from 'rxjs/operators';
import { INDEXED_DB_CONFIG } from '@app/app-indexed-db.const';
import { IndexedDbService } from '@shared/indexed-db/services';
import { OnlineRepository } from '@data-layer/repositories';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { TokenService } from '@services/token.service';

@Injectable({ providedIn: 'root' })
export class AppOfflineService {
  public isLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private isLoading$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private apiService: ApiService,
    private iDbService: IndexedDbService,
    private onlineRepository: OnlineRepository,
    private tokenService: TokenService,
  ) {}

  public loadOfflineData(): void {
    if (this.tokenService.isAuthenticated && this.tokenService.getRefreshToken() && !this.isLoaded$.getValue()) {
      this.loadAllData();
    }
  }

  public loadAllData(): void {
    // for now on app start it will clear all and load again
    // later we can play&see if we can freeze this data for one week for example

    if (this.isLoading$.getValue()) {
      return;
    }

    this.isLoading$.next(true);

    this.iDbService
      .dropAll()
      .pipe(
        switchMap(() =>
          combineLatest([
            this.onlineRepository.getAllCustomsCountries(),
            this.onlineRepository.getAllMarineInsuranceCountries(),
            this.onlineRepository.getAllIncoterms(),
            this.onlineRepository.getAllIsfUnits(),
            this.onlineRepository.getAllLineUnits(),
            this.onlineRepository.getAllShipmentUnits(),
            this.onlineRepository.getAllCurrencies(),
          ]),
        ),
        finalize(() => this.isLoading$.next(false)),
        take(1),
      )
      .subscribe(
        ([customsCountries, marineInsuranceCountries, incoterms, isfUnits, lineUnits, shipmentUnits, currencies]: [
          CountryModel[],
          CountryModel[],
          IncoTermModel[],
          UnitModel[],
          UnitModel[],
          UnitModel[],
          CurrencyModel[],
        ]) => {
          this.iDbService.addMultiple(INDEXED_DB_CONFIG.TABLES.CUSTOMS_COUNTRIES, customsCountries);
          this.iDbService.addMultiple(INDEXED_DB_CONFIG.TABLES.MARINE_INSURANCE_COUNTRIES, marineInsuranceCountries);
          this.iDbService.addMultiple(INDEXED_DB_CONFIG.TABLES.INCOTERMS, incoterms);
          this.iDbService.addMultiple(INDEXED_DB_CONFIG.TABLES.ISF_UNITS, isfUnits);
          this.iDbService.addMultiple(INDEXED_DB_CONFIG.TABLES.LINE_UNITS, lineUnits);
          this.iDbService.addMultiple(INDEXED_DB_CONFIG.TABLES.SHIPMENT_UNITS, shipmentUnits);
          this.iDbService.addMultiple(INDEXED_DB_CONFIG.TABLES.CURRENCIES, currencies);
          this.iDbService.addMultiple(INDEXED_DB_CONFIG.TABLES.CURRENCIES_BY_DATE, []);
          this.isLoaded$.next(true);
        },
      );
  }
}
